import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Copy, Check, Lock, Globe } from 'lucide-react';

const API_ENDPOINTS = [
  {
    category: 'Authentication',
    icon: '🔐',
    endpoints: [
      {
        method: 'POST',
        path: '/api/auth/register',
        description: 'Register a new user account',
        auth: false,
        body: { email: 'string', password: 'string', name: 'string' },
        response: { access_token: 'string', user: { id: 'string', email: 'string', name: 'string' } }
      },
      {
        method: 'POST',
        path: '/api/auth/login',
        description: 'Login with email and password',
        auth: false,
        body: { email: 'string', password: 'string' },
        response: { access_token: 'string', user: { id: 'string', email: 'string', name: 'string' } }
      },
      {
        method: 'POST',
        path: '/api/auth/magic-link/send',
        description: 'Send passwordless magic link to email',
        auth: false,
        body: { email: 'string' },
        response: { status: 'success', message: 'string' }
      },
      {
        method: 'POST',
        path: '/api/auth/magic-link/verify',
        description: 'Verify magic link token and login',
        auth: false,
        body: { token: 'string' },
        response: { access_token: 'string', user: { id: 'string', email: 'string' } }
      },
      {
        method: 'GET',
        path: '/api/auth/me',
        description: 'Get current authenticated user',
        auth: true,
        response: { id: 'string', email: 'string', name: 'string', subscription_plan: 'string?' }
      },
      {
        method: 'POST',
        path: '/api/auth/google-callback',
        description: 'Handle Google OAuth callback',
        auth: false,
        body: { credential: 'string' },
        response: { access_token: 'string', user: { id: 'string', email: 'string' }, is_new_user: 'boolean' }
      }
    ]
  },
  {
    category: 'Subscriptions & Usage',
    icon: '📊',
    endpoints: [
      {
        method: 'GET',
        path: '/api/subscription/status',
        description: 'Get detailed subscription status with usage limits',
        auth: true,
        response: {
          plan: 'free|student|pro',
          status: 'none|active|cancelled',
          limits: { max_lectures: 'number', max_subjects: 'number', ai_requests_per_day: 'number' },
          usage: {
            lectures: { current: 'number', limit: 'number', can_create: 'boolean' },
            subjects: { current: 'number', limit: 'number', can_create: 'boolean' },
            ai_requests: { used_today: 'number', limit: 'number', remaining: 'number' }
          },
          upgrade_needed: 'boolean'
        }
      },
      {
        method: 'GET',
        path: '/api/dashboard/stats',
        description: 'Get user dashboard statistics',
        auth: true,
        response: {
          total_lectures: 'number',
          total_subjects: 'number',
          total_study_hours: 'number',
          learning_bytes_count: 'number',
          upcoming_exams: 'number'
        }
      },
      {
        method: 'GET',
        path: '/api/progress',
        description: 'Get user study progress data',
        auth: true,
        response: {
          subject_progress: [{ subject_id: 'string', name: 'string', lectures: 'number' }],
          study_streak: 'number',
          recent_activity: 'array'
        }
      }
    ]
  },
  {
    category: 'Subjects',
    icon: '📁',
    endpoints: [
      {
        method: 'GET',
        path: '/api/subjects',
        description: 'Get all subjects for current user',
        auth: true,
        response: [{ id: 'string', name: 'string', color: 'string', created_at: 'datetime' }]
      },
      {
        method: 'POST',
        path: '/api/subjects',
        description: 'Create a new subject (limited by plan)',
        auth: true,
        body: { name: 'string', color: 'string?' },
        response: { id: 'string', name: 'string', color: 'string' },
        errors: ['403: Subject limit reached. Upgrade for unlimited subjects.']
      },
      {
        method: 'PUT',
        path: '/api/subjects/{id}',
        description: 'Update a subject',
        auth: true,
        body: { name: 'string?', color: 'string?' },
        response: { id: 'string', name: 'string', updated_at: 'datetime' }
      },
      {
        method: 'DELETE',
        path: '/api/subjects/{id}',
        description: 'Delete a subject and all its lectures',
        auth: true,
        response: { message: 'Subject deleted' }
      }
    ]
  },
  {
    category: 'Lectures',
    icon: '📚',
    endpoints: [
      {
        method: 'GET',
        path: '/api/lectures',
        description: 'Get all lectures for current user',
        auth: true,
        query: { subject_id: 'string?' },
        response: [{ id: 'string', title: 'string', transcript: 'string?', summary: 'string?', key_points: 'array?' }]
      },
      {
        method: 'POST',
        path: '/api/lectures',
        description: 'Create a new lecture (limited by plan)',
        auth: true,
        body: { subject_id: 'string', title: 'string', topic: 'string?', notes: 'string?', transcript: 'string?' },
        response: { id: 'string', title: 'string', created_at: 'datetime' },
        errors: ['403: Lecture limit reached. Upgrade for unlimited lectures.']
      },
      {
        method: 'PUT',
        path: '/api/lectures/{id}',
        description: 'Update a lecture',
        auth: true,
        body: { title: 'string?', notes: 'string?', transcript: 'string?' },
        response: { id: 'string', title: 'string', updated_at: 'datetime' }
      },
      {
        method: 'DELETE',
        path: '/api/lectures/{id}',
        description: 'Delete a lecture',
        auth: true,
        response: { message: 'Lecture deleted' }
      }
    ]
  },
  {
    category: 'AI Features',
    icon: '🤖',
    endpoints: [
      {
        method: 'POST',
        path: '/api/transcribe',
        description: 'Transcribe audio file using Whisper AI',
        auth: true,
        body: 'multipart/form-data: file (audio)',
        response: { transcript: 'string' }
      },
      {
        method: 'POST',
        path: '/api/simplify-text',
        description: 'Simplify complex text (Confusion Button)',
        auth: true,
        body: { transcript_snippet: 'string', timestamp: 'string?' },
        response: { simplified: 'string', original: 'string' }
      },
      {
        method: 'POST',
        path: '/api/summarize-lecture',
        description: 'Generate Cornell Notes summary',
        auth: true,
        body: { transcript: 'string', lecture_title: 'string?' },
        response: { summary: 'string', key_points: ['string'], cornell_notes: { main_ideas: 'array', cue_column: 'array' } }
      },
      {
        method: 'POST',
        path: '/api/study-buddy/chat',
        description: 'Chat with AI study buddy (rate limited)',
        auth: true,
        body: { messages: [{ role: 'user|assistant', content: 'string' }], user_context: 'object?' },
        response: { role: 'assistant', content: 'string', timestamp: 'datetime' },
        errors: ['AI request limit reached for today. Upgrade for more!']
      },
      {
        method: 'POST',
        path: '/api/study-buddy/study-plan',
        description: 'Generate personalized study plan',
        auth: true,
        body: { subject: 'string', exam_date: 'string?', topics: 'array?' },
        response: { study_plan: 'string', timeline: 'array' }
      },
      {
        method: 'POST',
        path: '/api/study-buddy/quiz',
        description: 'Generate adaptive quiz questions',
        auth: true,
        body: { subject: 'string', difficulty: 'easy|medium|hard', question_count: 'number' },
        response: { questions: [{ question: 'string', options: 'array', correct_answer: 'number' }] }
      },
      {
        method: 'POST',
        path: '/api/study-buddy/exam-prep',
        description: 'Generate exam preparation strategy',
        auth: true,
        body: { subject: 'string', exam_date: 'string', exam_type: 'midterm|final|quiz' },
        response: { strategy: 'string', days_until: 'number' }
      },
      {
        method: 'POST',
        path: '/api/learning-bytes/generate/{lecture_id}',
        description: 'Generate flashcards and quiz from lecture',
        auth: true,
        query: { num_flashcards: 'number?', num_quiz_questions: 'number?' },
        response: { flashcards: [{ front: 'string', back: 'string' }], quiz_questions: 'array' }
      },
      {
        method: 'GET',
        path: '/api/learning-bytes',
        description: 'Get all learning bytes for user',
        auth: true,
        query: { subject_id: 'string?', lecture_id: 'string?' },
        response: [{ id: 'string', type: 'flashcard|quiz', content: 'object' }]
      }
    ]
  },
  {
    category: 'Exams',
    icon: '📝',
    endpoints: [
      {
        method: 'GET',
        path: '/api/exams',
        description: 'Get all exams for current user',
        auth: true,
        query: { subject_id: 'string?', upcoming_only: 'boolean?' },
        response: [{ id: 'string', title: 'string', exam_date: 'datetime', completed: 'boolean' }]
      },
      {
        method: 'POST',
        path: '/api/exams',
        description: 'Schedule a new exam',
        auth: true,
        body: { subject_id: 'string', title: 'string', exam_date: 'datetime', exam_type: 'midterm|final|quiz', topics: 'array?' },
        response: { id: 'string', title: 'string', exam_date: 'datetime' }
      },
      {
        method: 'POST',
        path: '/api/exams/{id}/complete',
        description: 'Mark exam as completed with optional score',
        auth: true,
        body: { score: 'number?' },
        response: { message: 'Exam marked as completed', score: 'number?' }
      }
    ]
  },
  {
    category: 'Payments',
    icon: '💳',
    endpoints: [
      {
        method: 'GET',
        path: '/api/payments/plans',
        description: 'Get subscription plans with pricing',
        auth: false,
        query: { currency: 'USD|EUR|GBP|NGN|GHS|KES|ZAR' },
        response: { 
          plans: [{ id: 'string', name: 'string', monthly_price: 'number', features: 'array' }], 
          currency: 'string', 
          supported_currencies: 'array' 
        }
      },
      {
        method: 'GET',
        path: '/api/payments/currency-detect',
        description: 'Auto-detect user currency from location',
        auth: false,
        response: { currency: 'string', country: 'string', mobile_money_available: 'boolean', mobile_money_providers: 'array' }
      },
      {
        method: 'POST',
        path: '/api/payments/initialize',
        description: 'Initialize Paystack payment',
        auth: true,
        body: { email: 'string', plan_id: 'student|pro', interval: 'monthly|yearly', currency: 'string', channels: 'array?' },
        response: { status: 'boolean', data: { authorization_url: 'string', reference: 'string' } }
      },
      {
        method: 'GET',
        path: '/api/payments/verify/{reference}',
        description: 'Verify payment status',
        auth: true,
        response: { status: 'boolean', data: { status: 'success|failed', amount: 'number', channel: 'string' } }
      },
      {
        method: 'GET',
        path: '/api/payments/subscription',
        description: 'Get user current subscription',
        auth: true,
        response: { status: 'none|active|cancelled', plan: 'object?', interval: 'string?', next_payment_date: 'datetime?' }
      }
    ]
  },
  {
    category: 'Preferences',
    icon: '⚙️',
    endpoints: [
      {
        method: 'GET',
        path: '/api/preferences',
        description: 'Get user preferences',
        auth: true,
        response: { theme: 'string', accent_color: 'string', font_size: 'string', voice_character: 'string' }
      },
      {
        method: 'PUT',
        path: '/api/preferences',
        description: 'Update user preferences',
        auth: true,
        body: { theme: 'string?', accent_color: 'string?', font_size: 'string?', voice_character: 'string?' },
        response: { theme: 'string', accent_color: 'string', updated: 'boolean' }
      },
      {
        method: 'POST',
        path: '/api/preferences/background-upload',
        description: 'Upload custom background image',
        auth: true,
        body: 'multipart/form-data: file (image)',
        response: { url: 'string', message: 'string' }
      }
    ]
  },
  {
    category: 'Collaboration',
    icon: '👥',
    endpoints: [
      {
        method: 'POST',
        path: '/api/collaboration/share',
        description: 'Share a lecture with another user',
        auth: true,
        body: { lecture_id: 'string', email: 'string', permission: 'view|edit|comment' },
        response: { message: 'string', share_id: 'string', permission: 'string' }
      },
      {
        method: 'GET',
        path: '/api/collaboration/collaborators/{lecture_id}',
        description: 'Get all collaborators for a lecture',
        auth: true,
        response: { collaborators: [{ email: 'string', permission: 'string', accepted: 'boolean' }], count: 'number' }
      },
      {
        method: 'GET',
        path: '/api/collaboration/my-shared-lectures',
        description: 'Get lectures shared with current user',
        auth: true,
        response: { lectures: [{ lecture: 'object', permission: 'string', owner: { name: 'string', email: 'string' } }] }
      }
    ]
  },
  {
    category: 'Study Groups',
    icon: '👨‍👩‍👧‍👦',
    endpoints: [
      {
        method: 'POST',
        path: '/api/collaboration/groups',
        description: 'Create a new study group',
        auth: true,
        body: { name: 'string', description: 'string?', is_public: 'boolean' },
        response: { message: 'string', group: { id: 'string', name: 'string', members: 'array', member_count: 'number' } }
      },
      {
        method: 'GET',
        path: '/api/collaboration/groups',
        description: 'Get study groups the user belongs to',
        auth: true,
        response: { groups: [{ id: 'string', name: 'string', member_count: 'number', is_public: 'boolean' }], count: 'number' }
      },
      {
        method: 'GET',
        path: '/api/collaboration/groups/discover',
        description: 'Discover public study groups',
        auth: true,
        query: { subject_id: 'string?' },
        response: { groups: 'array', count: 'number' }
      },
      {
        method: 'GET',
        path: '/api/collaboration/groups/{group_id}',
        description: 'Get study group details',
        auth: true,
        response: { id: 'string', name: 'string', members: 'array', resource_count: 'number' }
      },
      {
        method: 'POST',
        path: '/api/collaboration/groups/{group_id}/join',
        description: 'Join a public study group',
        auth: true,
        response: { message: 'Joined group successfully' },
        errors: ['403: This group requires an invitation']
      },
      {
        method: 'POST',
        path: '/api/collaboration/groups/{group_id}/leave',
        description: 'Leave a study group',
        auth: true,
        response: { message: 'Left group successfully' },
        errors: ['400: Owner cannot leave. Transfer ownership or delete the group.']
      },
      {
        method: 'POST',
        path: '/api/collaboration/groups/{group_id}/invite',
        description: 'Invite a user to join a study group',
        auth: true,
        body: { email: 'string', role: 'member|admin' },
        response: { message: 'string', invitation_id: 'string' },
        errors: ['403: Only admins can invite members']
      },
      {
        method: 'POST',
        path: '/api/collaboration/groups/{group_id}/messages',
        description: 'Send a message to a study group',
        auth: true,
        body: { content: 'string', message_type: 'text|announcement' },
        response: { id: 'string', user_name: 'string', content: 'string', created_at: 'datetime' }
      },
      {
        method: 'GET',
        path: '/api/collaboration/groups/{group_id}/messages',
        description: 'Get messages from a study group',
        auth: true,
        query: { limit: 'number?' },
        response: { messages: 'array', count: 'number' }
      },
      {
        method: 'POST',
        path: '/api/collaboration/groups/{group_id}/resources',
        description: 'Share a lecture to a study group',
        auth: true,
        query: { lecture_id: 'string' },
        response: { message: 'string', resource_id: 'string' }
      },
      {
        method: 'DELETE',
        path: '/api/collaboration/groups/{group_id}',
        description: 'Delete a study group (owner only)',
        auth: true,
        response: { message: 'Group deleted' }
      }
    ]
  },
  {
    category: 'Notifications',
    icon: '🔔',
    endpoints: [
      {
        method: 'GET',
        path: '/api/notifications/in-app',
        description: 'Get in-app notifications for current user',
        auth: true,
        query: { limit: 'number?' },
        response: { notifications: [{ id: 'string', type: 'string', title: 'string', message: 'string', read: 'boolean', created_at: 'datetime' }], unread_count: 'number', total: 'number' }
      },
      {
        method: 'POST',
        path: '/api/notifications/in-app/{notification_id}/read',
        description: 'Mark a notification as read',
        auth: true,
        response: { message: 'Notification marked as read' }
      },
      {
        method: 'POST',
        path: '/api/notifications/in-app/read-all',
        description: 'Mark all notifications as read',
        auth: true,
        response: { message: 'All notifications marked as read' }
      },
      {
        method: 'DELETE',
        path: '/api/notifications/in-app/{notification_id}',
        description: 'Delete a notification',
        auth: true,
        response: { message: 'Notification deleted' }
      },
      {
        method: 'GET',
        path: '/api/notifications/vapid-public-key',
        description: 'Get VAPID public key for web push subscription',
        auth: false,
        response: { publicKey: 'string' }
      },
      {
        method: 'POST',
        path: '/api/notifications/subscribe',
        description: 'Subscribe to push notifications',
        auth: true,
        body: { endpoint: 'string', keys: { p256dh: 'string', auth: 'string' } },
        response: { message: 'string', subscription_id: 'string' }
      },
      {
        method: 'GET',
        path: '/api/notifications/settings',
        description: 'Get notification preferences',
        auth: true,
        response: { study_reminders: 'boolean', flashcard_alerts: 'boolean', exam_alerts: 'boolean', quiet_hours: { enabled: 'boolean', start: 'string', end: 'string' } }
      },
      {
        method: 'PUT',
        path: '/api/notifications/settings',
        description: 'Update notification preferences',
        auth: true,
        body: { study_reminders: 'boolean?', flashcard_alerts: 'boolean?', exam_alerts: 'boolean?' },
        response: { message: 'Settings updated' }
      }
    ]
  },
  {
    category: 'Gamification',
    icon: '🏆',
    endpoints: [
      {
        method: 'GET',
        path: '/api/gamification/profile',
        description: 'Get user gamification profile with XP and level',
        auth: true,
        response: { user_id: 'string', total_xp: 'number', level: { level: 'number', name: 'string', min_xp: 'number', max_xp: 'number', progress_percent: 'number' }, badge_count: 'number', badges: 'array' }
      },
      {
        method: 'GET',
        path: '/api/gamification/badges',
        description: 'Get all available badges with earned status',
        auth: true,
        response: { badges: [{ id: 'string', name: 'string', description: 'string', icon: 'string', xp_reward: 'number', earned: 'boolean', earned_at: 'datetime?' }] }
      },
      {
        method: 'POST',
        path: '/api/gamification/badges/check',
        description: 'Check and award any newly earned badges',
        auth: true,
        response: { new_badges: 'array', total_xp_earned: 'number' }
      },
      {
        method: 'GET',
        path: '/api/gamification/leaderboard',
        description: 'Get XP leaderboard',
        auth: true,
        query: { period: 'all|weekly|monthly', limit: 'number?' },
        response: { leaderboard: [{ rank: 'number', user_id: 'string', name: 'string', total_xp: 'number', level: 'number', level_name: 'string', badge_count: 'number', is_current_user: 'boolean' }], current_user_rank: 'number', period: 'string' }
      },
      {
        method: 'POST',
        path: '/api/gamification/xp/add',
        description: 'Add XP for an activity (internal)',
        auth: true,
        body: { activity_type: 'string', xp_amount: 'number' },
        response: { total_xp: 'number', xp_added: 'number', level_up: 'boolean?', new_level: 'number?' }
      }
    ]
  },
  {
    category: 'Referrals',
    icon: '🎁',
    endpoints: [
      {
        method: 'GET',
        path: '/api/referrals/code',
        description: 'Get user referral code and stats',
        auth: true,
        response: { code: 'string', referral_count: 'number', rewards_earned: 'number', pending_rewards: 'number' }
      },
      {
        method: 'POST',
        path: '/api/referrals/apply',
        description: 'Apply a referral code during signup',
        auth: true,
        body: { code: 'string' },
        response: { success: 'boolean', message: 'string', bonus: 'string?' },
        errors: ['400: Invalid referral code', '400: Cannot use your own code', '400: Referral already applied']
      },
      {
        method: 'GET',
        path: '/api/referrals/history',
        description: 'Get referral history',
        auth: true,
        response: { referrals: [{ referred_email: 'string', status: 'pending|completed', reward: 'string', created_at: 'datetime' }], total_earned: 'number' }
      }
    ]
  }
];

const MethodBadge = ({ method }) => {
  const colors = {
    GET: 'bg-green-500/20 text-green-400 border-green-500/30',
    POST: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    PUT: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    DELETE: 'bg-red-500/20 text-red-400 border-red-500/30'
  };
  
  return (
    <span className={`px-2 py-0.5 text-xs font-mono font-bold rounded border ${colors[method]}`}>
      {method}
    </span>
  );
};

const CodeBlock = ({ code, language = 'json' }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(code, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="relative bg-slate-900 rounded-lg border border-white/10 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-slate-800/50 border-b border-white/10">
        <span className="text-xs text-white/50">{language}</span>
        <button aria-label="Confirm"
          onClick={handleCopy}
          className="text-white/50 hover:text-white transition-colors"
        >
          {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
      <pre className="p-4 text-sm text-white/80 overflow-x-auto">
        <code>{JSON.stringify(code, null, 2)}</code>
      </pre>
    </div>
  );
};

const EndpointCard = ({ endpoint }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden bg-slate-800/30">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors"
      >
        <MethodBadge method={endpoint.method} />
        <code className="text-white font-mono text-sm flex-1 text-left">{endpoint.path}</code>
        {endpoint.auth ? (
          <Lock className="w-4 h-4 text-amber-400" title="Requires authentication" />
        ) : (
          <Globe className="w-4 h-4 text-green-400" title="Public endpoint" />
        )}
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-white/50" />
        ) : (
          <ChevronRight className="w-4 h-4 text-white/50" />
        )}
      </button>
      
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-white/10 pt-4">
          <p className="text-white/70 text-sm">{endpoint.description}</p>
          
          {endpoint.query && (
            <div>
              <h5 className="text-xs font-semibold text-white/50 uppercase mb-2">Query Parameters</h5>
              <CodeBlock code={endpoint.query} language="query" />
            </div>
          )}
          
          {endpoint.body && (
            <div>
              <h5 className="text-xs font-semibold text-white/50 uppercase mb-2">Request Body</h5>
              <CodeBlock code={endpoint.body} language="json" />
            </div>
          )}
          
          {endpoint.response && (
            <div>
              <h5 className="text-xs font-semibold text-white/50 uppercase mb-2">Response</h5>
              <CodeBlock code={endpoint.response} language="json" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const APIReference = () => {
  const [expandedCategory, setExpandedCategory] = useState('Authentication');
  
  return (
    <section className="py-16 bg-slate-950">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">API Reference</h2>
          <p className="text-white/60">
            Complete REST API documentation for iStudyPro
          </p>
        </div>
        
        {/* Base URL */}
        <div className="mb-8 p-4 bg-slate-800/50 rounded-xl border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-white/50 uppercase font-semibold">Base URL</span>
          </div>
          <code className="text-[#CCFF00] font-mono">https://your-app.emergentagent.com/api</code>
          <p className="text-white/50 text-sm mt-2">
            All endpoints require the <code className="text-white/70">/api</code> prefix
          </p>
        </div>
        
        {/* Authentication Info */}
        <div className="mb-8 p-4 bg-amber-500/10 rounded-xl border border-amber-500/20">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 font-semibold">Authentication</span>
          </div>
          <p className="text-white/70 text-sm">
            Protected endpoints require a Bearer token in the Authorization header:
          </p>
          <code className="block mt-2 text-sm text-white/80 bg-slate-900 p-2 rounded">
            Authorization: Bearer {'<your_access_token>'}
          </code>
        </div>
        
        {/* API Categories */}
        <div className="space-y-6">
          {API_ENDPOINTS.map((category) => (
            <div key={category.category} className="bg-slate-900/50 rounded-2xl border border-white/10 overflow-hidden">
              <button
                onClick={() => setExpandedCategory(
                  expandedCategory === category.category ? null : category.category
                )}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{category.icon}</span>
                  <h3 className="text-xl font-bold text-white">{category.category}</h3>
                  <span className="text-xs text-white/50 bg-white/10 px-2 py-0.5 rounded-full">
                    {category.endpoints.length} endpoints
                  </span>
                </div>
                {expandedCategory === category.category ? (
                  <ChevronDown className="w-5 h-5 text-white/50" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-white/50" />
                )}
              </button>
              
              {expandedCategory === category.category && (
                <div className="px-6 pb-6 space-y-3">
                  {category.endpoints.map((endpoint, idx) => (
                    <EndpointCard key={idx} endpoint={endpoint} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Rate Limits */}
        <div className="mt-12 p-6 bg-slate-800/50 rounded-2xl border border-white/10">
          <h3 className="text-xl font-bold text-white mb-4">Rate Limits</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-slate-900/50 rounded-xl">
              <div className="text-2xl font-bold text-white">100</div>
              <div className="text-white/60 text-sm">Requests/minute (Free)</div>
            </div>
            <div className="p-4 bg-slate-900/50 rounded-xl">
              <div className="text-2xl font-bold text-purple-400">500</div>
              <div className="text-white/60 text-sm">Requests/minute (Student)</div>
            </div>
            <div className="p-4 bg-slate-900/50 rounded-xl">
              <div className="text-2xl font-bold text-amber-400">Unlimited</div>
              <div className="text-white/60 text-sm">Requests/minute (Pro)</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default APIReference;
